
import React, { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader } from 'lucide-react';
import { MatchData } from '@/components/team-details/types/selector-types';

interface CompareHeaderProps {
  selectedSeasons: string[];
  onSeasonsChange: (seasons: string[]) => void;
  homeTeam: string;
  awayTeam: string;
}

const CompareHeader: React.FC<CompareHeaderProps> = ({
  selectedSeasons,
  onSeasonsChange,
  homeTeam,
  awayTeam
}) => {
  const {
    data: savedStates,
    isLoading
  } = useQuery({
    queryKey: ['seasons', homeTeam, awayTeam],
    queryFn: async () => {
      const {
        data,
        error
      } = await supabase.from('saved_states').select('year, league, data').order('year', {
        ascending: false
      });
      if (error) throw error;
      if (!homeTeam || !awayTeam) return [];

      const commonSeasons = data.filter(state => {
        if (!Array.isArray(state.data)) return false;

        const matches = state.data as MatchData[];
        const teams = new Set(matches.flatMap(match => [match.HomeTeam, match.AwayTeam]));

        const bothTeamsPresent = teams.has(homeTeam) && teams.has(awayTeam);
        const teamsPlayedTogether = matches.some(match => match.HomeTeam === homeTeam && match.AwayTeam === awayTeam || match.HomeTeam === awayTeam && match.AwayTeam === homeTeam);
        return bothTeamsPresent && teamsPlayedTogether;
      });

      return commonSeasons.map(state => ({
        year: state.year,
        league: state.league
      }));
    }
  });

  useEffect(() => {
    if (savedStates && savedStates.length > 0 && selectedSeasons.length === 0) {
      onSeasonsChange([savedStates[0].year]);
    }
  }, [savedStates, selectedSeasons.length, onSeasonsChange]);

  const handleSeasonToggle = (season: string) => {
    if (selectedSeasons.includes(season)) {
      onSeasonsChange(selectedSeasons.filter(s => s !== season));
    } else {
      const newSeasons = [...selectedSeasons, season].sort((a, b) => parseInt(a) - parseInt(b));
      onSeasonsChange(newSeasons);
    }
  };

  const handleSelectAll = () => {
    if (!savedStates) return;
    const sortedSeasons = savedStates.map(state => state.year).sort((a, b) => parseInt(a) - parseInt(b));
    onSeasonsChange(sortedSeasons);
  };

  const handleClearAll = () => {
    if (savedStates && savedStates.length > 0) {
      onSeasonsChange([savedStates[0].year]);
    }
  };

  const allSeasonsSelected = savedStates && selectedSeasons.length === savedStates.length;

  return <div className="text-center mb-8">
      <div className="flex justify-center items-center mb-4">
        <h2 className="text-2xl font-bold text-center">Team Comparison</h2>
      </div>
      
      <div className="space-y-4">
        <div className="flex justify-center space-x-2">
          <Button variant="outline" size="sm" onClick={handleSelectAll} disabled={isLoading || allSeasonsSelected}>
            {allSeasonsSelected ? 'All Seasons Selected' : 'Select All'}
          </Button>
          {selectedSeasons.length > 0 && <Button variant="ghost" size="sm" onClick={handleClearAll} className="text-red-500 hover:text-red-700">
              Clear Selection
            </Button>}
        </div>
        <div className="flex flex-wrap gap-2 justify-center p-4 border rounded-md">
          {isLoading ? <div className="flex items-center justify-center w-full py-2">
              <Loader className="h-4 w-4 animate-spin" />
            </div> : savedStates?.map(state => <div key={state.year} className="flex flex-col items-center gap-1">
                <Button variant={selectedSeasons.includes(state.year) ? "secondary" : "outline"} className="flex items-center gap-1" onClick={() => handleSeasonToggle(state.year)}>
                  {state.year}
                  {selectedSeasons.includes(state.year) && <Badge variant="secondary" className="ml-1 text-xs">Selected</Badge>}
                </Button>
                <span className="text-xs text-muted-foreground">{state.league}</span>
              </div>)}
        </div>
      </div>
    </div>;
};

export default CompareHeader;
